(async () => {
    const { Core } = await importModule('Core');

    // Same storage key/shape as vehicle-fitment-selector.js, so a vehicle picked here shows up
    // immediately on any product page's "Will This Fit My Vehicle?" widget too — one shared
    // vehicle, not two separate pickers the customer has to fill in twice.
    const VEHICLE_STORAGE_KEY = 'unleashed:selected-vehicle';
    const normalizeBoltPattern = (s) => String(s).toUpperCase().replace(/\s+/g, '');

    // Same year-coverage rule as vehicle-fitment-selector.js — kept in sync with the "current"
    // sentinel for still-in-production vehicles.
    const vehicleCoversYear = (v, year) => {
        if (v.year_start == null) return false;
        if (year < v.year_start) return false;
        if (v.year_end === 'current') return true;
        if (v.year_end == null) return year === v.year_start;
        return year <= v.year_end;
    };

    customElements.define('vehicle-shop-selector', class extends Core {

        elements = {
            makeSelect: '[data-shop-make]',
            yearSelect: '[data-shop-year]',
            modelSelect: '[data-shop-model]',
            goButton: '[data-shop-go]',
            formEl: '[data-shop-form]',
        }

        async render() {
            this.vehicles = [];

            try {
                const res = await fetch(this.getAttribute('data-shop-src'));
                this.vehicles = await res.json();
            } catch (e) {
                console.error('Shop-by-vehicle data failed to load', e);
                return;
            }

            this._populateMakes();
            this._bindEvents();
        }

        _populateMakes() {
            const makes = Array.from(new Set(this.vehicles.map(v => v.make))).sort();
            this.$makeSelect.innerHTML = '<option value="">Select Make</option>' +
                makes.map(m => `<option value="${this._esc(m)}">${this._esc(m)}</option>`).join('');
        }

        _bindEvents() {
            this.$makeSelect.addEventListener('change', () => this._onMakeChange());
            this.$yearSelect.addEventListener('change', () => this._onYearChange());
            this.$modelSelect.addEventListener('change', () => this._onModelChange());
            this.$formEl.addEventListener('submit', (e) => {
                e.preventDefault();
                this._go();
            });
        }

        _onMakeChange() {
            const make = this.$makeSelect.value;
            this.$modelSelect.innerHTML = '<option value="">Select Model</option>';
            this.$modelSelect.disabled = true;
            this.$goButton.disabled = true;

            if (!make) {
                this.$yearSelect.innerHTML = '<option value="">Select Year</option>';
                this.$yearSelect.disabled = true;
                return;
            }

            const maxYear = new Date().getFullYear() + 1;
            const years = new Set();
            this.vehicles.filter(v => v.make === make).forEach(v => {
                if (v.year_start == null) return;
                const end = v.year_end === 'current' ? maxYear : (v.year_end ?? v.year_start);
                for (let y = v.year_start; y <= end; y++) years.add(y);
            });

            this.$yearSelect.innerHTML = '<option value="">Select Year</option>' +
                Array.from(years).sort((a, b) => b - a).map(y => `<option value="${y}">${y}</option>`).join('');
            this.$yearSelect.disabled = false;
        }

        _onYearChange() {
            const make = this.$makeSelect.value;
            const year = parseInt(this.$yearSelect.value, 10);
            this.$goButton.disabled = true;

            if (!year) {
                this.$modelSelect.innerHTML = '<option value="">Select Model</option>';
                this.$modelSelect.disabled = true;
                return;
            }

            const models = Array.from(new Set(
                this.vehicles
                    .filter(v => v.make === make && vehicleCoversYear(v, year))
                    .map(v => v.model)
            )).sort();

            this.$modelSelect.innerHTML = '<option value="">Select Model</option>' +
                models.map(m => `<option value="${this._esc(m)}">${this._esc(m)}</option>`).join('');
            this.$modelSelect.disabled = false;
        }

        _onModelChange() {
            this.$goButton.disabled = !this.$modelSelect.value;
        }

        _esc(str) {
            const div = document.createElement('div');
            div.textContent = str;
            return div.innerHTML;
        }

        _go() {
            const make = this.$makeSelect.value;
            const year = parseInt(this.$yearSelect.value, 10);
            const model = this.$modelSelect.value;
            if (!make || !year || !model) return;

            const vehicle = this.vehicles.find(v => v.make === make && v.model === model && vehicleCoversYear(v, year));
            if (!vehicle) return;

            const vehicleLabel = `${year} ${make} ${model}`;

            const normalizedRealPatterns = Array.from(new Set((vehicle.bolt_patterns || []).map(normalizeBoltPattern)));

            // Some vehicles need a specific staggered size or a specific wheel model that only
            // bolt-pattern matching can't express (e.g. the C8 Z06 needs 20x10 front/21x13 rear
            // that almost nothing in the catalog comes in) — those get a dedicated, pre-curated
            // collection instead of the general bolt-pattern filtered results. boltPatterns is
            // still the vehicle's REAL pattern(s) here (product-variant-selector.js reads it
            // directly from localStorage on every page load to fade non-matching Bolt Pattern
            // options — blanking it here would break that everywhere, not just this collection).
            // restrictedCollectionHandle instead tells vehicle-filter-autocheck.js which single
            // collection page is already fully curated, so it can skip re-filtering only there.
            // Wheel diameter range (e.g. 19-20 inch) — a "Blank (Custom)" bolt pattern otherwise
            // matches ANY vehicle regardless of whether the wheel is even offered in a size that
            // clears the car (e.g. a 22"-only wheel showing as a fit for a car that tops out at
            // 20"). Not every vehicle has this data yet, so it's optional.
            const wheelDiameterMin = vehicle.wheel_diameter_min ?? null;
            const wheelDiameterMax = vehicle.wheel_diameter_max ?? null;

            // Some vehicles only clear with wheels from specific brands (fender/caliper/fitment
            // reasons bolt pattern + size alone can't express) — restrictedVendors/restrictedStrict
            // let vehicle-filter-autocheck.js narrow the general Wheels collection listing to just
            // those brands too, matching what vehicle-fitment-selector.js already enforces on each
            // product page.
            const restrictedVendors = vehicle.restricted_vendors || null;
            const restrictedStrict = vehicle.restricted_strict || false;

            localStorage.setItem(VEHICLE_STORAGE_KEY, JSON.stringify({
                year, make, model, boltPatterns: normalizedRealPatterns,
                restrictedCollectionHandle: vehicle.restricted_collection_handle || null,
                wheelDiameterMin, wheelDiameterMax,
                restrictedVendors, restrictedStrict,
                // Set for vehicles picked from the 4Play-only list (4play-fitment.json).
                fitmentSource: vehicle.source || null,
                // Vehicles whose catalog is blank/custom-drilled wheels only: boltPatterns above
                // stays the REAL pattern for fit checks, but the collection's Bolt Pattern filter
                // should pre-check just "Blank (Custom)" (see vehicle-filter-autocheck.js).
                blankOnly: vehicle.blank_only || false,
            }));

            if (vehicle.restricted_collection_handle) {
                const params = new URLSearchParams({ vehicle: vehicleLabel });
                window.location.href = `/collections/${vehicle.restricted_collection_handle}?${params.toString()}`;
                return;
            }

            // Always include Blank (Custom) alongside the vehicle's real pattern(s) since those
            // wheels are machined to order and fit any vehicle. vehicle-filter-autocheck.js
            // reads this marker and clicks the real "Bolt Pattern" filter checkboxes — a plain
            // ?filter.v.option.bolt+pattern=A&filter.v.option.bolt+pattern=B URL doesn't work
            // reliably on a fresh page load (Shopify's native collection filtering only keeps
            // the last repeated value there, even though the same params work once applied
            // through the filter UI's own request).
            const boltPatterns = vehicle.blank_only
                ? ['Blank (Custom)']
                : Array.from(new Set([...(vehicle.bolt_patterns || []), 'Blank (Custom)']));

            const params = new URLSearchParams({
                vehicle_fit: boltPatterns.join(','),
                vehicle: vehicleLabel,
            });
            if (wheelDiameterMin != null && wheelDiameterMax != null) {
                const sizes = [];
                for (let s = wheelDiameterMin; s <= wheelDiameterMax; s++) sizes.push(s);
                params.set('vehicle_size', sizes.join(','));
            }
            const destination = this.getAttribute('data-shop-destination') || '/collections/wheels';
            window.location.href = `${destination}?${params.toString()}`;
        }
    });
})();
